import { TranslateLoader } from '@ngx-translate/core';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { ENVIRONMENT_INJECTION_TOKEN } from '@ls/models-environment';
import { RegionalizationTranslationsApiService } from '@ls/api-regionalization';
import { Environment } from '../models/environment.model';
import { DataAccessRegionalizationTranslationsStore } from '../signal-stores/data-access-regionalization-translations.signal-store';
import {
  buildTranslationsFileName,
  checkShouldOverrideWithLocalTranslations,
  checkShouldUseSourceTranslations,
  loadMultipleTranslationsAndMergePreservingOrder,
} from '../utils/translations.utils';

type Translations = Record<string, string>;

@Injectable({
  providedIn: 'root',
})
export class TranslationsLoaderService extends TranslateLoader {
  private readonly environment = inject<Environment>(
    ENVIRONMENT_INJECTION_TOKEN
  );
  private readonly regionalizationTranslationsApiService = inject(
    RegionalizationTranslationsApiService
  );
  private readonly dataAccessRegionalizationTranslationsStore = inject(
    DataAccessRegionalizationTranslationsStore
  );

  public getTranslation(languageCode: string): Observable<Translations> {
    this.dataAccessRegionalizationTranslationsStore.reset();

    const translationsFileName = buildTranslationsFileName(languageCode);
    const loadTranslationsObservables = [];

    const shouldUseSourceTranslations =
      checkShouldUseSourceTranslations(languageCode);

    if (shouldUseSourceTranslations) {
      loadTranslationsObservables.push(
        this.loadLocalTranslations(translationsFileName)
      );

      return this.loadTranslations(loadTranslationsObservables);
    }

    loadTranslationsObservables.push(
      this.loadRemoteTranslations(translationsFileName)
    );

    const shouldOverrideWithLocalTranslations =
      checkShouldOverrideWithLocalTranslations(this.environment);

    if (shouldOverrideWithLocalTranslations) {
      loadTranslationsObservables.push(
        this.loadLocalTranslations(translationsFileName)
      );
    }

    return this.loadTranslations(loadTranslationsObservables);
  }

  private loadTranslations(
    loadTranslationsObservables: Observable<Translations>[]
  ): Observable<Translations> {
    this.dataAccessRegionalizationTranslationsStore.setLoadingTranslations(
      true
    );

    return loadMultipleTranslationsAndMergePreservingOrder(
      ...loadTranslationsObservables
    ).pipe(
      tap(() =>
        this.dataAccessRegionalizationTranslationsStore.setLoadingTranslations(
          false
        )
      )
    );
  }

  private loadLocalTranslations(
    translationsFileName: string
  ): Observable<Translations> {
    return this.regionalizationTranslationsApiService
      .loadLocalTranslations(translationsFileName)
      .pipe(catchError(() => of({})));
  }

  private loadRemoteTranslations(
    translationsFileName: string
  ): Observable<Translations> {
    return this.regionalizationTranslationsApiService
      .loadTranslations(translationsFileName)
      .pipe(
        catchError(() => {
          this.dataAccessRegionalizationTranslationsStore.setLoadTranslationsError(
            'DATA_ACCESS.REGIONALIZATION.TRANSLATIONS_LOADER_SERVICE.ERROR.FAILED_TO_LOAD_TRANSLATIONS'
          );

          return of({});
        })
      );
  }
}
